/**
 * Single source of truth for route paths. Import these instead of writing
 * string literals at call sites, so a URL change is a one-line edit here.
 *
 * ## What may go in a URL
 *
 * A path is the least private thing about a request. It is kept in browser
 * history, read by extensions, sent as `Referer` on the way to another origin,
 * and caught in any screenshot of the address bar — all of it outside the
 * app's control and none of it cleared by signing out.
 *
 * So a route param carries an opaque, backend-issued surrogate and nothing
 * else. `:clientId` stands for the `public_id` — a UUID — and never the
 * database key behind it. Two properties are what matter, and both have to
 * hold:
 *
 *   - Not enumerable. Sequential keys let anyone holding one URL walk the rest
 *     by counting, so every hit tells them a record exists. A random id gives
 *     no next value to try.
 *   - Not descriptive. No name, email, phone, or anything else read off the
 *     record, in a param or a query string. `?tab=` names a tab, which is why
 *     it is allowed to sit in the URL at all.
 *
 * On top of those, the id is enciphered into a token by `routeIds` before it
 * goes in the path, so the URL does not read as a record over a shoulder or in
 * a screenshot. That is a fourth layer and the weakest one — the key ships in
 * the bundle, so it stops a reader, not an attacker. Read the header of
 * `routeIds.ts` before relying on it for anything.
 *
 * None of this is the actual access control. The server decides that, and
 * already does: an id belonging to another workspace answers 404, not the
 * record (see `API_ENDPOINTS.clients.detail`). These keep a URL from being a
 * place to start looking — they do not stand in for the check behind it, and a
 * decoded id is as untrusted as one typed into the bar.
 *
 * `:planId` is a plan's `public_id`, the same way `:clientId` is a client's:
 * `POST /api/v1/meal-plans/` issues it, and the create dialog navigates
 * straight to it. The seeded fixtures keep their readable `seed-plan-N` until
 * the mock goes, which is the day a plan's meals can be fetched rather than
 * held in the browser by `plansService`.
 */
import { encodeRouteId } from "./routeIds";

export const ROUTES = {
  // Auth
  signup: "/signup",
  /**
   * A partner's referral link, as the backend builds it now:
   * `<FRONTEND_URL>/signup/<referral_code>`. The shape is the server's, not
   * this app's — it is stored on the partner and printed in their agreement —
   * so the route exists to match it rather than because the app would have
   * chosen to put a code in a path.
   *
   * It lands on the sign-up form rather than the login screen, which is the
   * whole point of the link: somebody following a partner's invitation does
   * not have an account yet, and the screen they are sent to should be the one
   * that makes them one.
   *
   * And the code does not stay in the URL. `ReferralLinkPage` parks it and
   * replaces this path with `/signup`, so the partner's code is not left in
   * the address bar, the history, or the `Referer` of every request the
   * sign-up screen makes — which is the rule the header of this file sets out,
   * applied to a path somebody else designed.
   */
  signupReferral: "/signup/:referralCode",
  /** The DPDP consent gate. Sits between the sign-up screen and the account
   *  actually being created, for both the email and the Google route in. */
  consent: "/consent",
  verifyOtp: "/verify-otp",
  login: "/login",
  /**
   * The same link in the shape the backend used to build it:
   * `<FRONTEND_URL>/login/<referral_code>`.
   *
   * Kept because links already in the world do not change when the generator
   * does. A partner's code sits in their signed agreement, in whatever they
   * have printed and in whatever they have already sent, and every one of
   * those still has to spend its code rather than quietly drop it.
   *
   * It parks the code and lands on the login screen, as it always did — the
   * screen that URL names. `signupReferral` above is where new links go.
   */
  loginReferral: "/login/:referralCode",
  /** Where Clerk returns the browser after a social sign-in. */
  ssoCallback: "/sso-callback",

  // App
  /**
   * The same terms put to a user who is already signed in - a document
   * published or republished since they last agreed.
   *
   * A separate path from `consent` above, and deliberately on this side of the
   * list: that one is closed to anyone signed in, and this is only ever reached
   * while signed in. One path serving both would redirect against itself.
   */
  consentRequired: "/consent-required",
  dashboard: "/dashboard",
  /**
   * The day's diary: every appointment booked with anybody, for one date.
   *
   * Singular — "my task", not "my tasks" — because that is what the design
   * calls it in the rail, and the rail and the URL should not disagree about
   * the name of a section. Nothing in the path but the word: the screen is one
   * day's list, and the day it is looking at is held on the screen rather than
   * in the URL. Which is a decision worth naming: a date in the path would
   * make a day linkable, and that is worth having the moment anybody wants to
   * send somebody else a day. It is not in yet because nothing asks for it.
   */
  myTask: "/my-task",
  /**
   * Booking an appointment, as a screen rather than as a dialog.
   *
   * Only a phone goes here. From `md` up the booking is a centred panel over
   * the day it is being booked into, which is right for a mouse — the day is
   * still on screen behind it, and the panel is one glance wide. On a phone
   * that panel was a drawer covering nine tenths of the screen with the
   * Schedule button at the far end of it, which is a page pretending not to
   * be one; so below `md` it is a page, with the phone's own back gesture as
   * its way out and the browser's history keeping the place it was opened
   * from.
   *
   * Static, so it wins over nothing — `myTask` takes no params — but named
   * `new` for the same reason `addClient` is: it is the one word this app
   * already uses for "a record that does not exist yet".
   *
   * The day it opens on rides in `?date=`, not in the path: see `dayParam`.
   */
  newTask: "/my-task/new",
  myClients: "/my-clients",
  // Static, so it wins over `myClientDetail` below: the router ranks a literal
  // segment above a param, and no client can be given the id "new".
  addClient: "/my-clients/new",
  myClientDetail: "/my-clients/:clientId",
  /**
   * The Add client form again, opened on a client who already exists.
   *
   * Under the detail path rather than beside it, so the URL says which record
   * is being edited — and a segment longer than `myClientDetail`, so the
   * router cannot confuse the two.
   */
  editClient: "/my-clients/:clientId/edit",
  enquiries: "/enquiries",
  // Static, so it wins over `enquiryDetail` below — same reasoning as
  // `addClient` above `myClientDetail`.
  addEnquiry: "/enquiries/new",
  enquiryDetail: "/enquiries/:enquiryId",
  /**
   * The Add enquiry form again, opened on an enquiry that already exists.
   *
   * Under the detail path rather than beside it, so the URL says which record
   * is being edited — and a segment longer than `enquiryDetail`, so the router
   * cannot confuse the two.
   */
  editEnquiry: "/enquiries/:enquiryId/edit",
  createPlan: "/create-plan",
  /** The plan builder, keyed on the id the plan list's rows carry. */
  planDetail: "/create-plan/:planId",
  /**
   * The supplement builder, keyed on the client it is being written for.
   *
   * On the client rather than on a record of its own, unlike `planDetail`
   * above: one pass of the screen writes several supplements at once, so there
   * is no single thing for the path to name until they are stored. It is only
   * ever opened from a client's Plans tab, which is where the id comes from.
   */
  createSupplement: "/create-supplement/:clientId",
  /**
   * The practice's own dishes - what it has written or uploaded, with the
   * macros each one carries.
   *
   * Nothing in the path but the word: the screen is one list, and a dish is
   * opened nowhere yet. No param to keep opaque, so nothing above applies.
   */
  dishes: "/dishes",
  revenue: "/revenue",
  myBrand: "/my-brand",
  createPage: "/create-page",
  launchApp: "/launch-app",
  /**
   * What the dietitian pays Naurist, as it used to be reached.
   *
   * No longer a screen: the plans on offer, the plan in force and the invoice
   * history are the Settings screen's Billings tab now — see
   * `BILLING_SETTINGS_PATH`. This path stays for two reasons, and redirects
   * there: it is in browser histories and bookmarks, and it is still the parent
   * of `billingCheckout` below, which is a screen of its own.
   */
  billing: "/billing",
  /**
   * The checkout for one plan — who it is billed to, and what it comes to.
   *
   * `:cycle` is the plain word: `/billing/yearly`. The only param in this file
   * that is not an opaque id, and deliberately so — everything the rule above
   * guards against needs the param to stand for a record. This one names a
   * product cycle out of a list of two that is the same for every user: there
   * is no next value to walk to, and nothing about a person in it. It is a
   * `?tab=` that happens to sit in the path.
   */
  billingCheckout: "/billing/:cycle",
  /**
   * How a checkout ended.
   *
   * A route rather than a step of the checkout for one reason: a card can be
   * authorised through a 3-D Secure redirect at the issuer, and the gateway
   * has to be given a `callback_url` to return to. What it returns to is a
   * fresh page load carrying no router state, so the outcome is handed over in
   * session storage — see `checkoutMachine`.
   */
  billingResult: "/billing/:cycle/result",
  /**
   * Where the browser lands after linking an account to a meeting app — one
   * path per app, and `MeetingProviderCallbackPage` draws both.
   *
   * Routes rather than steps of the Integrate apps card, for the reason the
   * billing result above is one: linking leaves the app entirely — out to
   * Google or Zoom, and only then back here — and what comes back is a fresh
   * page load carrying no router state and nothing of the card that started
   * it.
   *
   * Written out one per provider rather than as `/integrations/:slug/callback`
   * because each is registered with that provider per environment — Google's
   * against localhost, Zoom's against localhost, staging and production — and
   * a URL held somewhere else cannot move without being changed there too. A
   * `:slug` segment would also answer for providers this app has no page for.
   *
   * Under `/integrations/` rather than `/my-brand/` because that is what they
   * are: the end of a trip out of the app, not a tab of the brand screen —
   * and the page they draw sends people on to that tab.
   */
  googleMeetCallback: "/integrations/google/callback",
  zoomCallback: "/integrations/zoom/callback",
  /**
   * Where a new account lands: the free trial, offered before the app is.
   *
   * Three screens under one path — the plan, who it is billed to, and the
   * confirmation — and none of them is inside `AppLayout`, so the sidebar
   * appears for the first time on the dashboard the last of them leads to.
   * Signing up is the only thing that points here; see `VerifyOtpPage` and
   * `ConsentPage`.
   *
   * `/create-account` rather than `/start-trial`, because that is what these
   * three screens are to the person walking them: the last stretch of signing
   * up, not a separate offer they went looking for. The URL is read in the
   * address bar and in any link to it, and it should say where somebody is —
   * finishing an account — rather than name the thing being sold on the way.
   */
  trial: "/create-account",
  /**
   * The trial's own checkout: the details an invoice will need once the
   * fourteen days are up.
   *
   * `:cycle` is the plain word — `/create-account/yearly` — exactly as
   * `billingCheckout` carries it, and for the reason written there: it names
   * one of two product cycles rather than a record. Which cycle was picked on
   * the screen before is in the URL rather than in router state so a reload
   * does not drop somebody back to the start of the flow.
   */
  trialDetails: "/create-account/:cycle",
  /** The trial, started. Longer than the path above it, so the router ranks it
   *  first — the same ordering `addClient` gets above the client detail. */
  trialStarted: "/create-account/:cycle/started",
  settings: "/settings",
  /**
   * Picking a time for a support call — Cal's booker, on a screen of its own.
   *
   * Under `/settings` because that is the only way in: it is the second half of
   * the Support tab's "Book a call", not a destination of its own. A screen
   * rather than a dialog or a panel because a month of availability is a page's
   * worth of screen, and because a booking is a thing somebody leaves the
   * settings to do and comes back from.
   *
   * Nothing about the request is in the path. What was typed into the form
   * travels as router state — see `SupportTab` — since a reason and a message
   * are exactly the descriptive detail the note above this list forbids in a
   * URL. Reached with no state, the booker simply opens unfilled.
   */
  bookCall: "/settings/book-call",
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** Marketing-site pages that live outside this app. */
export const EXTERNAL_LINKS = {
  /**
   * The marketing site's front page — where someone goes when the app itself
   * has nothing to show them, as on a viewport it will not run at.
   */
  home: "https://naurist.com/",
  privacyPolicy: "https://naurist.com/privacy-policy",
  termsOfUse: "https://naurist.com/terms-of-use",
  dataProcessingAgreement: "https://naurist.com/data-processing-agreement",
  refundPolicy: "https://naurist.com/refund-policy",
} as const;

/**
 * Fills the `:clientId` param, so call sites never build the URL by hand.
 *
 * The id goes in enciphered — see `routeIds` — so the path does not read as a
 * record to anyone who happens to see the address bar. Pass the stored
 * `public_id`; the screen at the other end decodes it back with
 * `decodeRouteId`. Call sites deal in real ids only, and no call site should
 * encode one itself.
 *
 * The screen's tab is a `?tab=` on the end rather than state it holds alone,
 * so a link can point at one — which is what lets the plan builder send
 * someone back to the Plans tab they started from. `tab` is a
 * `ClientDetailTabId`; the union lives with the screen that renders it, and an
 * unknown value there simply opens on Details. It stays in the clear: it names
 * a tab, not a person.
 *
 * `kind` is the same again one level down, for the Plans tab's three lists —
 * "meal", "supplement" or "exercise". The supplement builder leaves the screen
 * and comes back to it, and without this it would come back to the Meal list
 * whichever list it was opened from. Only read under `tab=plans`, and an
 * unknown value opens on Meal, so it is safe to leave off.
 */
export const clientDetailPath = (
  clientId: string,
  tab?: string,
  kind?: string
) => {
  const token = encodeRouteId(clientId);
  if (!tab) return `${ROUTES.myClients}/${token}`;

  const query = kind ? `tab=${tab}&kind=${kind}` : `tab=${tab}`;
  return `${ROUTES.myClients}/${token}?${query}`;
};

/** The edit form for one client — the detail path with `/edit` on the end. */
export const clientEditPath = (clientId: string) =>
  `${ROUTES.myClients}/${encodeRouteId(clientId)}/edit`;

/** The same, for `:planId`. */
export const planDetailPath = (planId: string) =>
  `${ROUTES.createPlan}/${encodeRouteId(planId)}`;

/**
 * The supplement builder for one client — enciphered like every other id.
 *
 * `supplementId` opens it on one that already exists, as a `?supplement=`
 * rather than another segment: the screen is the same screen either way, and
 * the path already names the client it writes for. Enciphered too, for the
 * same reason the client is.
 */
export const createSupplementPath = (clientId: string, planId?: string) => {
  const path = `/create-supplement/${encodeRouteId(clientId)}`;
  return planId ? `${path}?plan=${encodeRouteId(planId)}` : path;
};

/** The same, for `:enquiryId`. */
export const enquiryDetailPath = (enquiryId: string) =>
  `${ROUTES.enquiries}/${encodeRouteId(enquiryId)}`;

/** The edit form for one enquiry — the detail path with `/edit` on the end. */
export const enquiryEditPath = (enquiryId: string) =>
  `${enquiryDetailPath(enquiryId)}/edit`;

/**
 * The checkout for one plan cycle — `billingCheckoutPath("yearly")`.
 *
 * No encoding, unlike the builders above: the cycle is a word from a fixed list
 * of two, not an id standing for a record. See `billingCheckout`.
 */
export const billingCheckoutPath = (cycle: string) =>
  `${ROUTES.billing}/${cycle}`;

/** How that checkout ended — the checkout path with `/result` on the end. */
export const billingResultPath = (cycle: string) =>
  `${billingCheckoutPath(cycle)}/result`;

/**
 * The trial's detail step for one cycle — `trialDetailsPath("yearly")`.
 *
 * No encoding, for the same reason `billingCheckoutPath` does none: the cycle
 * is a word from a fixed list of two, not an id standing for a record.
 */
export const trialDetailsPath = (cycle: string) => `${ROUTES.trial}/${cycle}`;

/** The trial, started — the detail path with `/started` on the end. */
export const trialStartedPath = (cycle: string) =>
  `${trialDetailsPath(cycle)}/started`;

/**
 * One tab of the Settings screen — `settingsTabPath("billings")`.
 *
 * The tab is a `?tab=` rather than a path of its own, the way a client's
 * detail tabs are, and for the same reason: it names a tab, not a record, so it
 * is allowed in the URL in the clear — and a link can point at one, which is
 * what everything below relies on.
 */
export const settingsTabPath = (tab: string) => `${ROUTES.settings}?tab=${tab}`;

/**
 * One tab of the My brand screen — `brandTabPath("plans")`.
 *
 * The same `?tab=` Settings carries, and allowed in the URL for the same
 * reason: it names a tab, not a record.
 */
export const brandTabPath = (tab: string) => `${ROUTES.myBrand}?tab=${tab}`;

/**
 * Where plans live: the My brand screen, opened on its Plans tab.
 *
 * The dashboard's setup checklist is what points at it — a step that says
 * "Create plans" has to land on the list that creates them, not on the
 * brand details beside it.
 */
export const BRAND_PLANS_PATH = brandTabPath("plans");

/**
 * Where services live: the My brand screen, opened on its Services tab.
 *
 * The other half of what the Add client and New enquiry gate asks for — a
 * prompt that says a service is missing has to land on the grid that makes
 * one. See `useSetupGate`.
 */
export const BRAND_SERVICES_PATH = brandTabPath("services");

/**
 * One tab of the My clients screen — `myClientsTabPath("renewal")`.
 *
 * The same `?tab=` again, on the roster this time: it names a tab, not a
 * client, so it is allowed in the URL in the clear.
 */
export const myClientsTabPath = (tab: string) =>
  `${ROUTES.myClients}?tab=${tab}`;

/**
 * Who is up for renewal: the My clients screen, opened on its Renewal tab.
 *
 * The dashboard's "clients renewal" figure is what points at it — a count of
 * the plans running out this week has to land on the list of them, not on the
 * whole roster.
 */
export const CLIENTS_RENEWAL_PATH = myClientsTabPath("renewal");

/**
 * Where billing lives: the Settings screen, opened on its Billings tab.
 *
 * Every way back to billing goes through this — the checkout's breadcrumb, the
 * result screen's way out, the redirect off `/billing` itself — so moving the
 * section again is a one-line edit here rather than a hunt through the feature.
 */
export const BILLING_SETTINGS_PATH = settingsTabPath("billings");

/**
 * The Billings tab, opened on the price list rather than on the plan somebody
 * is already on — `/settings?tab=billings&view=plans`.
 *
 * A subscriber's Billings tab opens on their own plan, which is right when they
 * arrive from the rail and wrong when they arrive from a checkout: Edit means
 * "let me pick a different term", and landing on the plan they already have
 * costs them another click to get back to the list. So the view travels in the
 * URL beside the tab, for the same reason the tab does — it names a view, not a
 * record, and a link can point at one.
 *
 * It is also what makes going *back* from the checkout land in the right place:
 * the list writes this into the address as soon as it is opened, so the entry
 * the browser returns to already says which view it was.
 */
export const BILLING_PLANS_PATH = `${BILLING_SETTINGS_PATH}&view=plans`;

/**
 * Where support lives: the Settings screen, opened on its Support tab.
 *
 * The way back from the booking screen, which is reached from nowhere else — so
 * the trail it draws leads to the tab that sent it, not to Settings in general.
 */
export const SUPPORT_SETTINGS_PATH = settingsTabPath("support");

/**
 * Where the Dish search setting lives: the My brand screen, opened on its
 * Configuration tab.
 *
 * The plan builder's switch points here twice — the "Settings" under it and
 * the "Learn more" on the card that introduces it — so both land on the tab
 * that explains the choice rather than on the brand's details.
 */
export const BRAND_CONFIGURATION_PATH = brandTabPath("configuration");


/**
 * The history-state flag that asks the Dishes screen to open its bulk upload
 * modal on arrival: `navigate(ROUTES.dishes, { state: { [OPEN_BULK_UPLOAD]: true } })`.
 *
 * For the screens that send somebody to Dishes to upload rather than to read
 * the list — My brand → Configuration cannot offer "My dishes only" to a
 * workspace with no dishes of its own, so its way out has to land on the
 * modal, not merely near it. The modal itself stays on Dishes, where the
 * importer and the report it writes already live; this is only the word for
 * the intent, and it lives beside the path so that both ends can say it
 * without either importing the other's screen.
 */
export const OPEN_BULK_UPLOAD = "openBulkUpload";
